/**
 * FilmAdminBoxOfficeProjections Page
 * Management page for box office projections with quick access to upcoming/recent films
 * and search for historical films
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { getFilmTrackingOverview } from '../../modules/FilmTracking/services/filmTrackingService';
import ProjectionModal from '../../components/BoxOfficeProjections/ProjectionModal';
import ProjectionsList from '../../components/BoxOfficeProjections/ProjectionsList';
import FilmSearch from '../../components/Assignment/FilmSearch';
import apiService from '../../lib/services/apiService';

const FilmAdminBoxOfficeProjections = () => {
  const [activeTab, setActiveTab] = useState(0); // 0 = Quick List, 1 = Search
  const [films, setFilms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFilm, setSelectedFilm] = useState(null);
  const [showProjectionModal, setShowProjectionModal] = useState(false);
  const [editingProjection, setEditingProjection] = useState(null);
  const [viewingFilm, setViewingFilm] = useState(null);
  const [viewingProjections, setViewingProjections] = useState([]);
  const [loadingProjections, setLoadingProjections] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);

  // Calculate date range: 2 weeks ago to 6 weeks from now
  const getDateRange = () => {
    const today = new Date();
    const twoWeeksAgo = new Date(today);
    twoWeeksAgo.setDate(today.getDate() - 14);
    const sixWeeksFromNow = new Date(today);
    sixWeeksFromNow.setDate(today.getDate() + 42);
    
    return {
      startDate: twoWeeksAgo.toISOString().split('T')[0],
      endDate: sixWeeksFromNow.toISOString().split('T')[0]
    };
  };

  // Fetch films for quick list
  const fetchFilms = async () => {
    setLoading(true);
    setError(null);
    try {
      const { startDate, endDate } = getDateRange();
      const result = await getFilmTrackingOverview({
        startDate,
        endDate,
        sortBy: 'release_date',
        sortOrder: 'asc',
        limit: 500
      });
      setFilms(result.films || []);
    } catch (err) {
      console.error('Error fetching films:', err);
      setError('Failed to load films. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch projections for a single film (on demand)
  const fetchFilmProjections = async (film) => {
    const filmId = film.imdb_id || film.imdbId || film.tt_id;
    if (!filmId) {
      setViewingProjections([]);
      return;
    }

    setLoadingProjections(true);
    try {
      const response = await apiService.getFilmBoxOfficeProjections(filmId);
      if (response.success && response.data) {
        setViewingProjections(response.data.projections || []);
      } else {
        setViewingProjections([]);
      }
    } catch (err) {
      console.error(`Error fetching projections for ${filmId}:`, err);
      setViewingProjections([]);
    } finally {
      setLoadingProjections(false);
    }
  };

  // Handle view projections
  const handleViewProjections = async (film) => {
    setViewingFilm(film);
    setShowViewDialog(true);
    await fetchFilmProjections(film);
  };

  // Handle close view dialog
  const handleCloseViewDialog = () => {
    setShowViewDialog(false);
    setViewingFilm(null);
    setViewingProjections([]);
  };

  useEffect(() => {
    if (activeTab === 0) {
      fetchFilms();
    }
  }, [activeTab]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleAddProjection = (film) => {
    setSelectedFilm(film);
    setEditingProjection(null);
    setShowProjectionModal(true);
  };


  const handleModalClose = () => {
    setShowProjectionModal(false);
    setSelectedFilm(null);
    setEditingProjection(null);
    // Refresh projections if viewing a film
    if (viewingFilm) {
      fetchFilmProjections(viewingFilm);
    }
  };

  const handleFilmSelect = (film) => {
    const filmWithId = {
      ...film,
      imdb_id: film.imdbId || film.imdb_id,
      imdbId: film.imdbId || film.imdb_id,
      title_text: film.title || film.title_text,
      release_date: film.releaseDate || film.release_date
    };
    handleAddProjection(filmWithId);
  };


  const formatReleaseDate = (dateStr) => {
    if (!dateStr) return 'TBA';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
        Box Office Projections Management
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage box office projections for upcoming and historical films
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Upcoming/Recent Films (Next 6 Weeks)" />
          <Tab label="Search Historical Films" />
        </Tabs>

        {/* Quick List Tab */}
        {activeTab === 0 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Films (Next 6 Weeks + Previous 2 Weeks)</Typography>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchFilms}
                disabled={loading}
              >
                Refresh
              </Button>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : films.length === 0 ? (
              <Alert severity="info">No films found in this date range.</Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Release Date</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {films.map((film) => {
                      const filmId = film.imdb_id || film.imdbId || film.tt_id;
                      const title = film.title_text || film.title || film.film_title || 'Unknown';

                      return (
                        <TableRow key={filmId} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {title}
                            </Typography>
                            {filmId && (
                              <Typography variant="caption" color="text.secondary">
                                {filmId}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{formatReleaseDate(film.release_date || film.releaseDate)}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="View Projections">
                              <IconButton
                                size="small"
                                onClick={() => handleViewProjections(film)}
                                color="primary"
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Add Projection">
                              <IconButton
                                size="small"
                                onClick={() => handleAddProjection(film)}
                                color="primary"
                              >
                                <AddIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* Search Tab */}
        {activeTab === 1 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Search for Historical Films
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Search for films to add box office projections. Click on a film to add a projection.
            </Typography>
            <FilmSearch onFilmSelect={handleFilmSelect} />
          </Box>
        )}
      </Paper>

      {/* Projection Modal */}
      {selectedFilm && (
        <ProjectionModal
          open={showProjectionModal}
          onClose={handleModalClose}
          projection={editingProjection}
          filmId={selectedFilm.imdb_id || selectedFilm.imdbId || selectedFilm.tt_id}
          filmTitle={selectedFilm.title_text || selectedFilm.title || selectedFilm.film_title || 'Unknown'}
          onSuccess={handleModalClose}
          canManage={true}
        />
      )}

      {/* View Projections Dialog */}
      <Dialog
        open={showViewDialog}
        onClose={handleCloseViewDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Box Office Projections
          {viewingFilm && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {viewingFilm.title_text || viewingFilm.title || viewingFilm.film_title || 'Unknown'}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {loadingProjections ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : viewingProjections.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                No Projections Available
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No box office projections have been created for this film yet.
              </Typography>
            </Box>
          ) : (
            <ProjectionsList
              projections={viewingProjections}
              filmId={viewingFilm?.imdb_id || viewingFilm?.imdbId || viewingFilm?.tt_id}
              filmTitle={viewingFilm?.title_text || viewingFilm?.title || viewingFilm?.film_title || 'Unknown'}
              canManage={true}
              onRefresh={() => viewingFilm && fetchFilmProjections(viewingFilm)}
              onEditProjection={(projection) => {
                setSelectedFilm(viewingFilm);
                setEditingProjection(projection);
                setShowViewDialog(false);
                setShowProjectionModal(true);
              }}
              loading={loadingProjections}
              error={null}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
          {viewingFilm && (
            <Button
              onClick={() => {
                handleCloseViewDialog();
                handleAddProjection(viewingFilm);
              }}
              variant="contained"
              startIcon={<AddIcon />}
            >
              Add Projection
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FilmAdminBoxOfficeProjections;
